// src/pages/SignUpPage.js
import React from 'react';
import { useSignUpForm } from '../hooks/useSignUpForm';
import { User, Phone, CalendarDays, ShieldCheck, ArrowRight, ArrowLeft, MessageSquareText, TimerReset, AlertCircle } from 'lucide-react';

import Config from "../Config.json";
import ICON from "../assets/icons/favicon.ico";
import logoImg from '../assets/images/logowhite.png'; 

const TITLE = `${Config.SITE_TITLE} - Finalizar Cadastro`;
const DESC = "Complete seu cadastro na Deli da Persy.";
const CANONICAL = `${Config.SITE_DOMAIN}/signup`; 

const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);
const MONTHS = [
    { value: 1, label: 'Janeiro' }, { value: 2, label: 'Fevereiro' }, { value: 3, label: 'Março' },
    { value: 4, label: 'Abril' }, { value: 5, label: 'Maio' }, { value: 6, label: 'Junho' },
    { value: 7, label: 'Julho' }, { value: 8, label: 'Agosto' }, { value: 9, label: 'Setembro' },
    { value: 10, label: 'Outubro' }, { value: 11, label: 'Novembro' }, { value: 12, label: 'Dezembro' }
];

function SignUpPage() {
    const {
        formData,
        isLoading,
        isSubmitting,
        isSendingCode,
        isPhoneValid,
        handlePhoneChange,
        handleBirthdayDayChange,
        handleBirthdayMonthChange,
        verificationStep,
        inputCode,
        setInputCode,
        initiateVerification,
        finalizeRegistration,
        cancelVerification,
        handleResendCode,
        verificationError,
        timeLeft
    } = useSignUpForm();

    const isStep1Valid = isPhoneValid && formData.birthdayDay !== "" && formData.birthdayMonth !== "";
    
    const formattedTime = `${String(Math.floor(timeLeft / 60000)).padStart(2, '0')}:${String(Math.floor((timeLeft % 60000) / 1000)).padStart(2, '0')}`;

    if (isLoading) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-[#FFF5EE]">
                <div className="w-12 h-12 border-4 border-[#dbcdac] border-t-[#528a89] rounded-full animate-spin mb-4"></div>
                <p className="text-[#528a89] font-serif text-lg animate-pulse">Preparando sua ficha...</p>
            </div>
        );
    }

    const inputBaseClass = "w-full bg-white border border-[#dbcdac]/60 rounded-xl px-4 py-3 text-stone-700 placeholder:text-stone-400 focus:outline-none focus:border-[#528a89] focus:ring-2 focus:ring-[#528a89]/10 transition-all shadow-sm";
    const readOnlyClass = "bg-stone-100/70 border-stone-200 text-stone-500 cursor-not-allowed shadow-none";

    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-[#FFF5EE] p-4 md:p-8 relative overflow-hidden"> 
            <title>{TITLE}</title>
            <link rel="icon" href={ICON} />
            <link rel="canonical" href={CANONICAL} />
            
            <div className="absolute top-0 left-0 w-full h-full opacity-[0.03] pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/aged-paper.png')]" />

            <div className="w-full max-w-lg bg-white rounded-[2rem] shadow-[0_20px_60px_-15px_rgba(0,0,0,0.1)] relative z-10 overflow-hidden animate-in fade-in zoom-in-[0.98] duration-500">
                
                {/* Header (Logo + Progress) */}
                <div className="bg-[#528a89] p-8 text-center relative overflow-hidden flex flex-col items-center">
                    <div className="absolute -top-10 -right-10 w-32 h-32 bg-white/10 rounded-full blur-2xl pointer-events-none"></div>
                    <img src={logoImg} alt="Deli da Persy Logo" className="w-28 md:w-32 drop-shadow-xl relative z-10 mb-6" />
                    
                    
                    <div className="relative z-10 flex items-center justify-center gap-3">
                        <div className={`flex items-center gap-2 text-sm font-medium ${!verificationStep ? 'text-white' : 'text-white/50'}`}>
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${!verificationStep ? 'bg-[#960051] text-white' : 'bg-white/20'}`}>1</span>
                            <span className="hidden sm:inline">Perfil</span>
                        </div>
                        <div className="w-8 h-[1px] bg-white/30"></div>
                        <div className={`flex items-center gap-2 text-sm font-medium ${verificationStep ? 'text-white' : 'text-white/50'}`}>
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${verificationStep ? 'bg-[#960051] text-white' : 'bg-white/20'}`}>2</span>
                            <span className="hidden sm:inline">Verificação</span>
                        </div>
                    </div>
                </div>

                <div className="p-6 md:p-10">
                    
                    {/* Exibição Centralizada de Erros */}
                    {verificationError && (
                        <div className="mb-6 bg-[#960051]/10 border border-[#960051]/20 text-[#960051] text-sm font-medium px-4 py-3 rounded-xl flex items-start gap-3 animate-in slide-in-from-top-2">
                            <AlertCircle size={18} className="shrink-0 mt-0.5" />
                            <p>{verificationError}</p>
                        </div>
                    )}

                    {!verificationStep ? (
                        /* ==================================================== */
                        /* STEP 1: PROFILE COMPLETION                           */
                        /* ==================================================== */
                        <div className="animate-in slide-in-from-left-4 duration-300">
                            <div className="text-center mb-8">
                                <h2 className="font-serif text-3xl text-[#960051] font-bold mb-2">Quase lá!</h2>
                                <p className="text-stone-500 font-light text-sm md:text-base leading-relaxed">
                                    Falta só mais um passo para finalizar sua conta. Precisamos de algumas informações básicas.
                                </p>
                            </div>

                            <form onSubmit={initiateVerification} className="space-y-8">
                                <div className="space-y-4">
                                    <h3 className="text-xs font-bold uppercase tracking-widest text-[#528a89] flex items-center gap-2">
                                        <User size={14}/> Dados da Conta
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <input type="text" value={formData.nome} readOnly className={`${inputBaseClass} ${readOnlyClass}`} />
                                        <input type="email" value={formData.email} readOnly className={`${inputBaseClass} ${readOnlyClass}`} />
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <h3 className="text-xs font-bold uppercase tracking-widest text-[#528a89] flex items-center gap-2">
                                        <Phone size={14}/> Informações de Contato
                                    </h3>
                                    <div>
                                        <input 
                                            type="tel" 
                                            value={formData.telefone} 
                                            onChange={handlePhoneChange} 
                                            placeholder="Seu WhatsApp: (XX) XXXXX-XXXX"
                                            required 
                                            className={`${inputBaseClass} ${formData.telefone.length > 0 && !isPhoneValid ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20' : ''}`}
                                        />
                                        <p className="text-xs text-stone-400 mt-2 flex items-center gap-1.5">
                                            <ShieldCheck size={12} className="text-[#dbcdac]" /> Usaremos seu telefone para reservas e comunicação.
                                        </p>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <h3 className="text-xs font-bold uppercase tracking-widest text-[#528a89] flex items-center gap-2">
                                        <CalendarDays size={14}/> Dia do seu Aniversário
                                    </h3>
                                    <div className="grid grid-cols-2 gap-4">
                                        <select value={formData.birthdayDay} onChange={handleBirthdayDayChange} required className={`${inputBaseClass} cursor-pointer text-center appearance-none bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23528a89%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-no-repeat bg-[position:right_1rem_center] bg-[length:1.2em_1.2em] pr-8`}>
                                            <option value="" disabled>Dia</option>
                                            {DAYS.map(d => <option key={d} value={d}>{d}</option>)}
                                        </select>
                                        
                                        <select value={formData.birthdayMonth} onChange={handleBirthdayMonthChange} required className={`${inputBaseClass} cursor-pointer appearance-none bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23528a89%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-no-repeat bg-[position:right_1rem_center] bg-[length:1.2em_1.2em] pr-8`}>
                                            <option value="" disabled>Mês</option>
                                            {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                                        </select>
                                    </div>
                                </div>

                                <button 
                                    type="submit" 
                                    disabled={!isStep1Valid || isSendingCode}
                                    className="w-full mt-4 py-4 bg-[#528a89] text-[#FFF5EE] rounded-xl font-bold tracking-wider text-lg hover:bg-[#3a6362] hover:-translate-y-1 transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:hover:translate-y-0 disabled:cursor-not-allowed"
                                >
                                    {isSendingCode ? 'Enviando...' : 'Continuar'} 
                                    {!isSendingCode && <ArrowRight size={20} />}
                                </button>
                            </form>
                        </div>

                    ) : (

                        /* ==================================================== */
                        /* STEP 2: PHONE VERIFICATION                           */
                        /* ==================================================== */
                        <div className="animate-in slide-in-from-right-4 duration-300">
                            <div className="text-center mb-8">
                                <div className="mx-auto w-16 h-16 bg-[#528a89]/10 rounded-full flex items-center justify-center mb-4">
                                    <MessageSquareText size={28} className="text-[#528a89]" />
                                </div>
                                <h2 className="font-serif text-3xl text-[#960051] font-bold mb-2">Verificar Telefone</h2>
                                <p className="text-stone-500 font-light text-sm md:text-base leading-relaxed">
                                    Enviamos um código de 3 dígitos para o WhatsApp <br/>
                                    <strong className="text-[#528a89] font-bold">{formData.telefone}</strong>
                                </p>
                            </div>
                            
                            <form onSubmit={finalizeRegistration} className="space-y-6 flex flex-col items-center">
                                
                                <div className="w-full flex flex-col items-center">
                                    <input 
                                        type="text" 
                                        maxLength="3" // Limitado a 3 dígitos conforme a API
                                        value={inputCode} 
                                        onChange={(e) => setInputCode(e.target.value.replace(/\D/g, ""))} 
                                        placeholder="• • •"
                                        autoFocus
                                        className="w-40 text-center text-4xl tracking-[0.5em] font-bold text-[#528a89] bg-[#FFF5EE]/50 border-2 border-[#dbcdac]/60 rounded-2xl py-4 pl-4 focus:outline-none focus:border-[#960051] focus:ring-4 focus:ring-[#960051]/10 transition-all shadow-inner placeholder:text-stone-300"
                                    />
                                    
                                    {/* Timer UI */}
                                    <div className="mt-4 h-6">
                                        {timeLeft > 0 ? (
                                            <p className="text-stone-500 text-sm font-medium flex items-center gap-1.5">
                                                <TimerReset size={16} className="text-[#dbcdac]"/> Código expira em {formattedTime}
                                            </p>
                                        ) : (
                                            <p className="text-[#960051] text-sm font-bold flex items-center gap-1.5">
                                                Código expirado.
                                            </p>
                                        )}
                                    </div>
                                </div>

                                <div className="w-full flex flex-col gap-3 mt-4">
                                    <button 
                                        type="submit" 
                                        disabled={isSubmitting || inputCode.length < 3 || timeLeft === 0}
                                        className="w-full py-4 bg-[#960051] text-[#FFF5EE] rounded-xl font-bold tracking-wider text-lg hover:bg-[#7a0e42] hover:-translate-y-1 transition-all shadow-lg flex items-center justify-center disabled:opacity-50 disabled:hover:translate-y-0 disabled:cursor-not-allowed"
                                    >
                                        {isSubmitting ? 'Verificando...' : 'Confirmar Código'}
                                    </button>
                                    
                                    {timeLeft === 0 && (
                                        <button 
                                            type="button" 
                                            onClick={handleResendCode}
                                            disabled={isSendingCode}
                                            className="w-full py-3 border-2 border-[#528a89] text-[#528a89] rounded-xl font-bold hover:bg-[#528a89]/10 transition-colors flex items-center justify-center disabled:opacity-50"
                                        >
                                            {isSendingCode ? 'Enviando...' : 'Reenviar Código'}
                                        </button>
                                    )}

                                    <button 
                                        type="button" 
                                        onClick={cancelVerification}
                                        className="w-full py-3 text-stone-500 text-sm font-medium hover:text-[#960051] transition-colors flex items-center justify-center gap-2"
                                    >
                                        <ArrowLeft size={16} /> Voltar e corrigir telefone
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default SignUpPage;